import { Link } from '@/components/LocalizedLink';
import SEOHead from '@/components/SEOHead';
import type { ReactNode } from 'react';
import type { LegalSection } from '@/lib/legalContent';
import { LEGAL_CONTACT_EMAIL, LEGAL_EFFECTIVE_DATE, legalContactLines } from '@/lib/legalContent';

type LegalDocPageProps = {
  title: string;
  subtitle: string;
  canonical: string;
  seoDescription: string;
  sections: LegalSection[];
  lang: 'tr' | 'en';
  showContactBlock?: boolean;
  children?: ReactNode;
};

const LegalDocPage = ({
  title,
  subtitle,
  canonical,
  seoDescription,
  sections,
  lang,
  showContactBlock = true,
  children,
}: LegalDocPageProps) => {
  const isTr = lang === 'tr';

  return (
    <main className="pt-32 pb-20">
      <SEOHead title={title} description={seoDescription} canonical={canonical} />

      <div className="container mx-auto max-w-4xl px-6 lg:px-12">
        <Link
          to="/"
          className="mb-8 inline-flex rounded-full border border-border px-4 py-2 text-sm font-body text-muted-foreground transition-colors hover:text-foreground"
        >
          {isTr ? 'Ana sayfaya dön' : 'Back to homepage'}
        </Link>

        <header className="mb-10">
          <p className="mb-4 font-body text-xs uppercase tracking-[0.25em] text-muted-foreground">{isTr ? 'Yasal' : 'Legal'}</p>
          <h1 className="mb-5 font-display text-4xl font-bold text-foreground lg:text-6xl">{title}</h1>
          <p className="font-body text-base leading-relaxed text-muted-foreground">{subtitle}</p>
          <p className="mt-4 font-body text-xs text-muted-foreground">
            {isTr ? 'Yürürlük tarihi' : 'Effective date'}:{' '}
            <time dateTime={LEGAL_EFFECTIVE_DATE}>
              {new Date(`${LEGAL_EFFECTIVE_DATE}T12:00:00`).toLocaleDateString(isTr ? 'tr-TR' : 'en-US', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </time>
          </p>
        </header>

        <div className="space-y-8">
          {sections.map((section) => (
            <section
              id={section.id}
              key={section.id ?? section.heading}
              className="rounded-2xl border border-border/70 bg-card/40 p-6"
            >
              <h2 className="mb-3 font-display text-2xl text-foreground">{section.heading}</h2>
              {section.paragraphs?.map((paragraph) => (
                <p key={paragraph} className="mb-3 font-body text-sm leading-relaxed text-muted-foreground last:mb-0">
                  {paragraph}
                </p>
              ))}
              {section.bullets ? (
                <ul className="space-y-2">
                  {section.bullets.map((bullet) => (
                    <li key={bullet} className="font-body text-sm leading-relaxed text-muted-foreground">
                      • {bullet}
                    </li>
                  ))}
                </ul>
              ) : null}
            </section>
          ))}
        </div>

        {children ? <div className="mt-8 space-y-8">{children}</div> : null}

        {showContactBlock ? (
          <section className="mt-8 rounded-2xl border border-border/70 bg-card/40 p-6">
            <h2 className="mb-3 font-display text-2xl text-foreground">{isTr ? 'İletişim' : 'Contact'}</h2>
            <p className="mb-3 font-body text-sm leading-relaxed text-muted-foreground">
              {isTr
                ? 'Bu metinle ilgili başvuru ve sorularınız için:'
                : 'For applications and questions regarding this document:'}
            </p>
            <div className="space-y-1 font-body text-sm text-muted-foreground">
              {legalContactLines(lang).map((line) => (
                <p key={line}>{line}</p>
              ))}
              <a
                href={`mailto:${LEGAL_CONTACT_EMAIL}`}
                className="mt-2 inline-flex text-foreground underline underline-offset-4"
              >
                {LEGAL_CONTACT_EMAIL}
              </a>
            </div>
          </section>
        ) : null}
      </div>
    </main>
  );
};

export default LegalDocPage;
